/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";

import { Tooltip } from "@/subframe/components/Tooltip";
import { IconButton } from "@/subframe/components/IconButton";
import SubframeCore from "@subframe/core";


import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import { RootState, AppDispatch } from "../redux/store";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import { sendTextMessage } from "../redux/reducers/messengerSlice";
import SelectDateAndTimePopup from '../components/subcomponents/SelectDateAndTimePopup';
import FileSelectionMenu from "./media/FileSelectionMenu";
import { addNotification } from "../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../Utils/notificationEnums";
import VoiceMessageHandler from "./media/VoiceMessageHandler";
import moment from "moment";

interface TextMessageSendProps {
  currentRecipient: Recipient | null;
  handleAiToHuman: () => void;
}

function TextMessageSend({
  currentRecipient,
  handleAiToHuman
}: TextMessageSendProps) {
  const dispatch = useDispatch<AppDispatch>();
  const system_user_id = useSelector(
    (state: RootState) => state.auth.system_user_id
  );
  const [isSending, setIsSending] = useState<boolean>(false);

  const [messageText, setMessageText] = useState("");
  const [isDateTimePopupOpen, setIsDateTimePopupOpen] = useState(false);

  const handleSendMessage = async (scheduledTime?: number, text?: string) => {
    // If text is not provided, use the messageText from state
    const messageToSend = text || messageText;
    
    if (!messageToSend.trim() || !system_user_id || !currentRecipient) return;
  
    try {
      setIsSending(true);
      await dispatch(
        sendTextMessage({

          text: messageToSend,
          currentRecipient,
          // Include scheduled_time only if it's provided
          ...(scheduledTime ? { scheduled_time: scheduledTime } : {})
        })
      ).unwrap();
      setIsSending(false);
  
      // Clear the input after successful send
      setMessageText("");
      
      // Show different notifications based on message type
      if (scheduledTime) {
        // For scheduled messages
        const formattedDateTime = moment(scheduledTime).format('MMM D, YYYY hh:mm A');
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Message Scheduled",
            message: `Your message will be sent on ${formattedDateTime}`,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        // For instant messages
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Message Sent",
            message: "Your message has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }
      
      handleAiToHuman();
    } catch (error: any) {
      setIsSending(false);
      
      // Error notification can also be different based on scheduled vs. instant
      const errorTitle = scheduledTime ? "Schedule Failed" : "Message Not Sent";
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: errorTitle,
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );
    }
  };


  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <div className="w-full bg-white border-t border-solid border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-end gap-2">
            <SubframeCore.DropdownMenu.Root>
              <SubframeCore.DropdownMenu.Trigger asChild={true}>
                <IconButton
                  variant="neutral-secondary"
                  icon="FeatherPaperclip"
                  className="w-8 h-8 text-gray-500 hover:text-gray-700"
                />
              </SubframeCore.DropdownMenu.Trigger>
              <SubframeCore.DropdownMenu.Portal>
                <SubframeCore.DropdownMenu.Content
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  asChild={true}
                >
                  <DropdownMenu className="min-w-[200px]">
                    <FileSelectionMenu currentRecipient={currentRecipient}/>                  
                  </DropdownMenu>
                </SubframeCore.DropdownMenu.Content>
              </SubframeCore.DropdownMenu.Portal>
            </SubframeCore.DropdownMenu.Root>

            <input
              type="text"
              placeholder="Type a message..."
              value={messageText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMessageText(e.target.value)
              }
              onKeyPress={handleKeyPress}
              className="flex-1 min-h-[36px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-100 focus:outline-none"
            />

            <VoiceMessageHandler currentRecipient={currentRecipient} />

            <SubframeCore.Tooltip.Provider>
              <SubframeCore.Tooltip.Root>
                <SubframeCore.Tooltip.Trigger asChild={true}>
                  <IconButton
                    variant="neutral-secondary"
                    icon="FeatherCalendarClock"
                    onClick={() => setIsDateTimePopupOpen(true)}
                    className="w-8 h-8 text-gray-500 hover:text-gray-700"
                  />
                </SubframeCore.Tooltip.Trigger>
                <SubframeCore.Tooltip.Portal>
                  <SubframeCore.Tooltip.Content
                    side="bottom"
                    align="center"
                    sideOffset={4}
                    asChild={true}
                  >
                    <Tooltip>Schedule send</Tooltip>
                  </SubframeCore.Tooltip.Content>
                </SubframeCore.Tooltip.Portal>
              </SubframeCore.Tooltip.Root>
            </SubframeCore.Tooltip.Provider>

            <IconButton
              variant="brand-primary"
              icon="FeatherSendHorizontal"
              onClick={() => handleSendMessage()}
              disabled={
                !messageText.trim() ||
                !system_user_id ||
                !currentRecipient ||
                isSending
              }
              className={`w-8 h-8 rounded-lg ${
                !messageText.trim() || !system_user_id || !currentRecipient || isSending
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            />
          </div>


        </div>
      </div>
      
      <SelectDateAndTimePopup
        open={isDateTimePopupOpen}
        setSelectDateAndTimePopupOpen={setIsDateTimePopupOpen}
        onScheduleMessage={handleSendMessage}
        messageText={messageText}
      />
    </>
  );
}

export default TextMessageSend;